// After a deploy, clients holding a stale index.html can request
// Vite chunk filenames that no longer exist on the CDN. The dynamic
// import rejects and surfaces as one of the patterns below. Forcing a
// one-shot reload makes the browser fetch the new index and the new
// chunk manifest.
//
// A cooldown guards against an infinite reload loop in the (rare) case
// where the failure isn't transient.

const RELOAD_COOLDOWN_MS = 10_000;
const RELOAD_TIMESTAMP_KEY = "gc.chunkLoadLastReload";

const CHUNK_LOAD_PATTERNS: RegExp[] = [
  /Failed to fetch dynamically imported module/i,
  /error loading dynamically imported module/i,
  /Importing a module script failed/i,
];

function isChunkLoadError(error: unknown): boolean {
  if (!error) {
    return false;
  }
  if (error instanceof Error) {
    if (error.name === "ChunkLoadError") {
      return true;
    }
    return CHUNK_LOAD_PATTERNS.some((p) => p.test(error.message ?? ""));
  }
  if (typeof error === "string") {
    return CHUNK_LOAD_PATTERNS.some((p) => p.test(error));
  }
  return false;
}

function attemptReload(): void {
  try {
    const last = sessionStorage.getItem(RELOAD_TIMESTAMP_KEY);
    if (last && Date.now() - Number(last) < RELOAD_COOLDOWN_MS) {
      return;
    }
    sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
  } catch {
    // sessionStorage may be unavailable (incognito quota, disabled cookies).
    // Fall through and reload anyway — an extra reload is better than a
    // permanently broken page.
  }
  window.location.reload();
}

window.addEventListener("error", (event) => {
  if (isChunkLoadError(event.error)) {
    attemptReload();
  }
});

window.addEventListener("unhandledrejection", (event) => {
  if (isChunkLoadError(event.reason)) {
    attemptReload();
  }
});
